import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { gql } from 'graphql-request'

import { useTheme } from '@/components/ThemeProvider'
import { useMutation } from '@hooks/graphql'
import { useAnalytics } from '@contexts/analytics'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import SplitLayout from '@/components/Onboarding/Layout/SplitLayout'
import { useFlashMessage } from '@/components/FlashMessage'

const CREATE_CLASSROOM_MUTATION = gql`
  mutation createClassroom($createClassroomInput: CreateClassroomInput!) {
    createClassroom(input: $createClassroomInput) {
      classroom {
        id
        name
      }
    }
  }
`

const CreateClassroom = ({ nextStep, completeOnboarding }) => {
  const formSchema = z.object({
    name: z.string().nonempty({ message: 'Classroom Name is required' })
  })

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: ''
    }
  })

  const { track } = useAnalytics()
  const { setFlashMessage } = useFlashMessage()
  const { resolvedTheme } = useTheme()

  const { mutate: createClassroom, isLoading: isCreating } = useMutation({
    gqlMutation: CREATE_CLASSROOM_MUTATION,
    onSuccess: () => {
      track('Educator Onboarding Step Completed', { step: 'classroom' })
      completeOnboarding()
    },
    onError: (error) => {
      setFlashMessage('Something went wrong', 'error')
    }
  })

  const onSubmit = (data) => {
    createClassroom({ createClassroomInput: data })
  }

  return (
    <SplitLayout>
      <h1 className='text-2xl font-extrabold tracking-tight my-1'>
        Create your first classroom
      </h1>
      <p className='my-2'>
        Classrooms are collaborative spaces where educators guide students through learning pathways and track progress.
      </p>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6 mt-6'>
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Classroom Name</FormLabel>
                <FormControl>
                  <Input placeholder='Classroom Name' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className='flex gap-2 mt-8'>
            <Button type='submit' loading={isCreating}>Continue</Button>
          </div>
        </form>
      </Form>

      <SplitLayout.SidePanel>
        <Choose>
          <When condition={resolvedTheme === 'dark'}>
            <div className='h-full w-full bg-right bg-no-repeat bg-contain bg-[url(/onboarding/classroom-info-dark@2x.png)]' />
          </When>
          <Otherwise>
            <div className='h-full w-full bg-right bg-no-repeat bg-contain bg-[url(/onboarding/classroom-info@2x.png)]' />
          </Otherwise>
        </Choose>
      </SplitLayout.SidePanel>
    </SplitLayout>
  )
}

export default CreateClassroom
